import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { ApexOptions } from 'apexcharts'
import { useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { BsArrowRight } from 'react-icons/bs'
import { FaCaretDown, FaCaretUp, FaRegFilePdf } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { RootState } from '~/app/store'
import { useAuth } from '~/hooks/useAuth'
import { FUJIFILM_COMPANY_ID } from '../../../app/constants'
import { useLocale } from '../../../hooks/useLocale'
export interface Score {
  score: string
  comment: string
}

export interface LeadershipValue {
  title: string
  scoreComments: {
    self: Score[]
    managers: Score[]
    peers: Score[]
    directReports: Score[]
  }
  group?: string
}

export interface CoachingTheme {
  title: string
}

export interface SelfReportedAnswers {
  coachingThemes: {
    question: string
    answer: CoachingTheme[]
  }
  limitingBeliefs: {
    question: string
    answer: string
  }
  empoweringBeliefs: {
    question: string
    answer: string
  }
  openEnded?: {
    question: string
    answer: string
  }[]
}

export interface SelfReportedFeedback {
  title: string
  feedback: string
}

export interface RespondentOpenEndedAnswer {
  title: string
  managers: string[]
  peers: string[]
  directReports: string[]
}

export interface TableData {
  value: string
  ratings: {
    self: { start: number; end: number }
    managers: { start: number; end: number }
    peers: { start: number; end: number }
    directReports: { start: number; end: number }
  }
}

export interface ChartItem {
  leadershipValue: {
    _id: string
    title: string
    value: string
  }
  startOfJourney: number[]
  endOfJourney: number[]
}

const getScoreColor = (score: string) => {
  const [value] = score.split('/').map(Number)
  if (value <= 2)
    return {
      text: '#B25300',
      bg: '#FFF4EB',
    }
  if (value <= 4)
    return {
      text: '#1C7AEB',
      bg: '#E8F1FD',
    }
  return {
    text: '#058A62',
    bg: '#E7F8F3',
  }
}

const ScoreCard = ({
  title,
  scores,
}: {
  title: string
  scores: Array<{ score: string; comment: string }>
}) => {
  if (scores.length === 0) return null
  return (
    <Box
      mt={2}
      p={4}
      borderRadius="md"
      bg="white"
      border="1px solid"
      borderColor="#D9DDE0"
    >
      <Text fontSize="14px" fontWeight="700" color="#58595A">
        {title}
      </Text>

      <Box mt="12px" display="flex" flexDirection="column" gap="16px">
        {scores.length === 0 && (
          <Text fontSize="14px" color="#58595A" textAlign="center">
            No data
          </Text>
        )}

        {scores.map(({ score, comment }, index) => {
          const { text, bg } = getScoreColor(score)
          return (
            <Flex key={index} gap="16px" alignItems="flex-start">
              <Box
                display="inline-block"
                px={3}
                py={1}
                borderRadius="md"
                bg={bg}
                height="fit-content"
              >
                <Text fontSize="16px" fontWeight="700" color={text}>
                  {score}
                </Text>
              </Box>
              <Text fontSize="14px" color="#58595A">
                {comment}
              </Text>
            </Flex>
          )
        })}
      </Box>
    </Box>
  )
}

const showStartEndChange = (start: number, end: number) => {
  if (!start || !end || start === end) return null
  if (start < end) return <FaCaretUp size={16} color="#38A383" />
  return <FaCaretDown size={16} color="#E60D00" />
}

const processTableCell = (value: number) => {
  if (value === 0) return 'N/A'
  return value
}

const TableView = ({ data }: { data: TableData[] }) => {
  if (!data) return null
  return (
    <Box mb={4}>
      <Box bg="#F3F4F6" p={4} borderRadius="md">
        <Grid templateColumns="2fr 1fr 1fr 1fr 1fr" gap={4}>
          <Text fontWeight="600" fontSize="14px">
            Value
          </Text>
          <Text fontWeight="600" fontSize="14px">
            Self rating
          </Text>
          <Text fontWeight="600" fontSize="14px">
            Manager(s) rating
          </Text>
          <Text fontWeight="600" fontSize="14px">
            Peer(s) rating
          </Text>
          <Text fontWeight="600" fontSize="14px">
            Reportee(s) rating
          </Text>
        </Grid>
      </Box>

      <Box>
        {data.map((row, index) => (
          <Grid
            key={index}
            templateColumns="2fr 1fr 1fr 1fr 1fr"
            gap={4}
            p={4}
            borderBottom="1px solid"
            borderColor="#E5E7EB"
          >
            <Text fontSize="14px" color="#111827">
              {row.value}
            </Text>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.self.start)}
              </Text>
              <BsArrowRight size={14} color="#111827" />
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.self.end)}
              </Text>
              {showStartEndChange(row.ratings.self.start, row.ratings.self.end)}
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.managers.start)}
              </Text>
              <BsArrowRight size={14} color="#111827" />
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.managers.end)}
              </Text>
              {showStartEndChange(
                row.ratings.managers.start,
                row.ratings.managers.end
              )}
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.peers.start)}
              </Text>
              <BsArrowRight size={14} color="#111827" />
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.peers.end)}
              </Text>
              {showStartEndChange(
                row.ratings.peers.start,
                row.ratings.peers.end
              )}
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.directReports.start)}
              </Text>
              <BsArrowRight size={14} color="#111827" />
              <Text fontSize="14px" color="#111827">
                {processTableCell(row.ratings.directReports.end)}
              </Text>
              {showStartEndChange(
                row.ratings.directReports.start,
                row.ratings.directReports.end
              )}
            </Flex>
          </Grid>
        ))}
      </Box>
    </Box>
  )
}

const EndOfJourneyReport = () => {
  const { t } = useLocale()
  const { user } = useAuth()
  const [showTable, setShowTable] = useState(false)
  const leadershipScores = useDisclosure({ defaultIsOpen: true })
  const selfReportedAnswers = useDisclosure()
  const respondentAnswers = useDisclosure()
  const selfFeedback = useDisclosure()
  const token = useSelector((state: RootState) => state.auth.token)

  const { data: endOfJourneyReport, isLoading } = useQuery({
    queryKey: ['end-of-journey-report'],
    async queryFn() {
      const res = await fetch(
        import.meta.env.VITE_API_ENDPOINT + 'v2/user/end-journey/results',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const output = await res.json()

      return output as {
        leadershipValues: LeadershipValue[]
        selfReported: SelfReportedAnswers
        respondentOpenEndedAnswers: RespondentOpenEndedAnswer[]
        tableData: TableData[]
        chartData: ChartItem[]
        selfReportedFeedback: SelfReportedFeedback[]
        reportUrl: string
        canShowResults?: boolean
        resultsPendingLabel?: string
      }
    },
  })

  const chartData = endOfJourneyReport?.chartData?.map(
    (chartItem: ChartItem) => ({
      leadershipValue: chartItem.leadershipValue,
      baseOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false,
          },
        },
        title: {
          text: chartItem.leadershipValue.title,
          align: 'center',
          style: {
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#111827',
            fontFamily: 'inherit',
            // Add CSS text wrapping properties
            wordWrap: 'break-word',
            width: '100%',
          },
          offsetY: 10,
          margin: 10,
          floating: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 7,
            borderRadiusApplication: 'end',
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 1,
          colors: ['transparent'],
        },
        grid: {
          show: true,
          borderColor: '#E5E7EB',
          strokeDashArray: 5,
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        xaxis: {
          categories: ['Self', 'Manager(s)', 'Peer(s)', 'Reportee(s)'],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          min: 0,
          max: 6,
          tickAmount: 6,
          labels: {
            formatter: (val: number) => Math.floor(val).toString(),
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val: number) => val.toString(),
          },
        },
      },
      series: [
        {
          name: 'Start of Journey',
          data: chartItem.startOfJourney,
          color: '#FF7E0C',
        },
        {
          name: 'End of Journey',
          data: chartItem.endOfJourney,
          color: '#3B8CEE',
        },
      ],
    })
  )

  if (isLoading) return <Spinner />

  if (!endOfJourneyReport) return <Text>No data</Text>

  if (endOfJourneyReport.canShowResults === false)
    return (
      <Text style={{ padding: 20, marginTop: 10, textAlign: 'center' }}>
        {endOfJourneyReport.resultsPendingLabel}
      </Text>
    )

  return (
    <Box px={4} py={2} mt="24px">
      <Box>
        <Flex
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={leadershipScores.onToggle}
        >
          <Box
            transform={
              leadershipScores.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
            }
            transition="transform 0.2s"
          >
            <Box
              w="24px"
              h="24px"
              bg="blue.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M6 8.78L2.47 5.25L3.53 4.19L6 6.66L8.47 4.19L9.53 5.25L6 8.78Z"
                  fill="#1C7AEB"
                />
              </Box>
            </Box>
          </Box>
          <Text fontSize="18px" fontWeight="700" flex="1">
            {t('leadership_value_scores')}
          </Text>
          {!!endOfJourneyReport.reportUrl && (
            <Button
              leftIcon={<FaRegFilePdf />}
              colorScheme="primary"
              aria-label="View detailed report"
              onClick={() =>
                window.open(endOfJourneyReport.reportUrl, '_blank')
              }
              disabled={!endOfJourneyReport.reportUrl}
            >
              {t('view_detailed_report')}
            </Button>
          )}
        </Flex>

        {leadershipScores.isOpen && (
          <Box mt={6}>
            <Flex justifyContent="flex-end" display="flex" mb={4}>
              <Box>
                <Button
                  size="sm"
                  colorScheme="primary"
                  variant={showTable ? 'outline' : 'solid'}
                  borderRightRadius={0}
                  onClick={() => setShowTable(false)}
                >
                  {t('chart_view')}
                </Button>
                <Button
                  size="sm"
                  colorScheme="primary"
                  variant={showTable ? 'solid' : 'outline'}
                  borderLeftRadius={0}
                  onClick={() => setShowTable(true)}
                >
                  {t('table_view')}
                </Button>
              </Box>
            </Flex>

            <Box>
              {!showTable ? (
                <Box width="100%" overflowX="hidden">
                  <Flex justifyContent="center" mb={8} gap={8}>
                    <Flex alignItems="center">
                      <Box
                        width="12px"
                        height="12px"
                        borderRadius="sm"
                        bg="#FF7E0C"
                        mr={2}
                      ></Box>
                      <Text fontSize="12px">Start of Journey result</Text>
                    </Flex>
                    <Flex alignItems="center">
                      <Box
                        width="12px"
                        height="12px"
                        borderRadius="sm"
                        bg="#3B8CEE"
                        mr={2}
                      ></Box>
                      <Text fontSize="12px">End of Journey result</Text>
                    </Flex>
                  </Flex>
                  <Grid
                    templateColumns={{
                      base: '1fr', // 1 column on mobile
                      md: 'repeat(auto-fit, minmax(250px, 1fr))', // Responsive columns that fit available space
                    }}
                    gap={4}
                    width="100%"
                  >
                    {chartData?.map((option: any) => (
                      <Box
                        key={option.leadershipValue._id}
                        px={3}
                        borderRadius="md"
                        // border="1px solid"
                        // borderColor="gray.200"
                        // bg="white"
                      >
                        <Text
                          fontSize="14px"
                          fontWeight="bold"
                          textAlign="center"
                          mb={2}
                          px={2}
                          lineHeight="1.3"
                          minHeight="36px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {option.leadershipValue.title}
                        </Text>
                        <ReactApexChart
                          options={
                            {
                              ...option.baseOptions,
                              title: { text: undefined }, // Remove ApexCharts title since we're using our own
                            } as ApexOptions
                          }
                          series={option.series}
                          type="bar"
                          height={325} // Slightly reduced to accommodate our custom title
                        />
                      </Box>
                    ))}
                  </Grid>
                </Box>
              ) : (
                <TableView data={endOfJourneyReport.tableData} />
              )}
            </Box>

            <Box fontSize="12px" pt="4px">
              {user?.company?._id === FUJIFILM_COMPANY_ID ? (
                <ul>
                  <li>
                    1 -{' '}
                    <i>
                      {t('self-score-1-description-fujifilm').replace(
                        '1 -',
                        ''
                      )}
                    </i>
                  </li>
                  <li>
                    2 -{' '}
                    <i>
                      {t('self-score-2-description-fujifilm').replace(
                        '2 -',
                        ''
                      )}
                    </i>
                  </li>
                  <li>
                    3 -{' '}
                    <i>
                      {t('self-score-3-description-fujifilm').replace(
                        '3 -',
                        ''
                      )}
                    </i>
                  </li>
                  <li>
                    4 -{' '}
                    <i>
                      {t('self-score-4-description-fujifilm').replace(
                        '4 -',
                        ''
                      )}
                    </i>
                  </li>
                  <li>
                    5 -{' '}
                    <i>
                      {t('self-score-5-description-fujifilm').replace(
                        '5 -',
                        ''
                      )}
                    </i>
                  </li>
                </ul>
              ) : (
                <ul>
                  <li>
                    1 -{' '}
                    <i>{t('self-score-1-description').replace('1 -', '')}</i>
                  </li>
                  <li>
                    2 -{' '}
                    <i>{t('self-score-2-description').replace('2 -', '')}</i>
                  </li>
                  <li>
                    3 -{' '}
                    <i>{t('self-score-3-description').replace('3 -', '')}</i>
                  </li>
                  <li>
                    4 -{' '}
                    <i>{t('self-score-4-description').replace('4 -', '')}</i>
                  </li>
                  <li>
                    5 -{' '}
                    <i>{t('self-score-5-description').replace('5 -', '')}</i>
                  </li>
                  <li>
                    6 -{' '}
                    <i>{t('self-score-6-description').replace('6 -', '')}</i>
                  </li>
                </ul>
              )}
            </Box>

            <Text fontSize="18px" fontWeight={700} mt="32px">
              {t('leadership_values_score_comments')}
            </Text>

            {endOfJourneyReport.leadershipValues?.map(
              (leadershipValue, index) => {
                if (
                  leadershipValue.scoreComments.self.length === 0 &&
                  leadershipValue.scoreComments.managers.length === 0 &&
                  leadershipValue.scoreComments.peers.length === 0 &&
                  leadershipValue.scoreComments.directReports.length === 0
                )
                  return null

                return (
                  <Box key={index} mt={4}>
                    <Text fontSize="16px" fontWeight={700}>
                      {leadershipValue.title}
                    </Text>
                    <Grid
                      mt="16px"
                      templateColumns={{
                        base: '1fr',
                        md: '1fr 1fr',
                      }}
                      gap="16px"
                    >
                      <ScoreCard
                        title="Self"
                        scores={leadershipValue.scoreComments.self}
                      />
                      <ScoreCard
                        title="Manager(s)"
                        scores={leadershipValue.scoreComments.managers}
                      />
                      <ScoreCard
                        title="Peer(s)"
                        scores={leadershipValue.scoreComments.peers}
                      />
                      <ScoreCard
                        title="Direct report(s)"
                        scores={leadershipValue.scoreComments.directReports}
                      />
                    </Grid>
                  </Box>
                )
              }
            )}
          </Box>
        )}
      </Box>

      <Divider my={4} />

      <Box>
        <Flex
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={selfReportedAnswers.onToggle}
        >
          <Box
            transform={
              selfReportedAnswers.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
            }
            transition="transform 0.2s"
          >
            <Box
              w="24px"
              h="24px"
              bg="blue.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M6 8.78L2.47 5.25L3.53 4.19L6 6.66L8.47 4.19L9.53 5.25L6 8.78Z"
                  fill="#1C7AEB"
                />
              </Box>
            </Box>
          </Box>
          <Text fontSize="18px" fontWeight="700">
            {t('self_reported_answers')}
          </Text>
        </Flex>

        {selfReportedAnswers.isOpen && (
          <Box mt={6}>
            <Text fontSize="16px" fontWeight="700" mb={4}>
              Coaching themes
            </Text>

            <Grid
              templateColumns={{
                base: '1fr',
                md: 'repeat(3, 1fr)',
              }}
              gap={4}
            >
              {endOfJourneyReport.selfReported?.coachingThemes.answer.map(
                (theme, index) => (
                  <Box
                    key={index}
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#D9DDE0"
                  >
                    <Text fontSize="14px">{theme.title}</Text>
                  </Box>
                )
              )}
            </Grid>

            {(
              endOfJourneyReport.selfReported?.openEnded ??
              [
                endOfJourneyReport.selfReported?.limitingBeliefs,
                endOfJourneyReport.selfReported?.empoweringBeliefs,
              ].filter(Boolean)
            ).map((item, index) => (
              <Box mt={8} key={index}>
                <Text fontSize="16px" fontWeight="700" mb={3}>
                  {item?.question}
                </Text>
                <Text fontSize="14px" color="#58595A">
                  {item?.answer}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Divider my={4} />

      <Box>
        <Flex
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={respondentAnswers.onToggle}
        >
          <Box
            transform={
              respondentAnswers.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
            }
            transition="transform 0.2s"
          >
            <Box
              w="24px"
              h="24px"
              bg="blue.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M6 8.78L2.47 5.25L3.53 4.19L6 6.66L8.47 4.19L9.53 5.25L6 8.78Z"
                  fill="#1C7AEB"
                />
              </Box>
            </Box>
          </Box>
          <Text fontSize="18px" fontWeight="700">
            {t('respondent_open_ended_answers')}
          </Text>
        </Flex>

        {respondentAnswers.isOpen && (
          <Box mt={6}>
            {endOfJourneyReport.respondentOpenEndedAnswers?.map((answer) => {
              return (
                <Box key={answer.title} mb={8}>
                  <Text fontSize="16px" fontWeight="700" mb={4}>
                    {answer.title}
                  </Text>
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      md: 'repeat(3, 1fr)',
                    }}
                    gap={4}
                  >
                    {answer.managers.length > 0 ? (
                      <Box
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="#D9DDE0"
                      >
                        <Text fontSize="14px" fontWeight="600" mb={3}>
                          Manager(s)
                        </Text>
                        <UnorderedList>
                          {answer.managers.map((text, index) => (
                            <ListItem
                              key={index}
                              fontSize="14px"
                              color="#58595A"
                              mb={index !== answer.managers.length - 1 ? 3 : 0}
                            >
                              {text}
                            </ListItem>
                          ))}
                        </UnorderedList>
                      </Box>
                    ) : null}

                    {answer.peers.length > 0 ? (
                      <Box
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="#D9DDE0"
                      >
                        <Text fontSize="14px" fontWeight="600" mb={3}>
                          Peer(s)
                        </Text>
                        <UnorderedList>
                          {answer.peers.map((text, index) => (
                            <ListItem
                              key={index}
                              fontSize="14px"
                              color="#58595A"
                              mb={index !== answer.peers.length - 1 ? 3 : 0}
                            >
                              {text}
                            </ListItem>
                          ))}
                        </UnorderedList>
                      </Box>
                    ) : null}

                    {answer.directReports.length > 0 ? (
                      <Box
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="#D9DDE0"
                      >
                        <Text fontSize="14px" fontWeight="600" mb={3}>
                          Direct report(s)
                        </Text>
                        <UnorderedList>
                          {answer.directReports.map((text, index) => (
                            <ListItem
                              key={index}
                              fontSize="14px"
                              color="#58595A"
                              mb={
                                index !== answer.directReports.length - 1
                                  ? 3
                                  : 0
                              }
                            >
                              {text}
                            </ListItem>
                          ))}
                        </UnorderedList>
                      </Box>
                    ) : null}
                  </Grid>
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      <Divider my={4} />

      <Box>
        <Flex
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={selfFeedback.onToggle}
        >
          <Box
            transform={selfFeedback.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
            transition="transform 0.2s"
          >
            <Box
              w="24px"
              h="24px"
              bg="blue.50"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M6 8.78L2.47 5.25L3.53 4.19L6 6.66L8.47 4.19L9.53 5.25L6 8.78Z"
                  fill="#1C7AEB"
                />
              </Box>
            </Box>
          </Box>
          <Text fontSize="18px" fontWeight="700">
            {t('self_reported_feedback')}
          </Text>
        </Flex>

        {selfFeedback.isOpen && (
          <Box mt={6} mb={3}>
            {endOfJourneyReport.selfReportedFeedback?.map((feedback) => (
              <Box key={feedback.title}>
                <Text fontSize="16px" fontWeight="700" mb={3}>
                  {feedback.title}
                </Text>
                <Text fontSize="14px" color="#58595A">
                  {feedback.feedback}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default EndOfJourneyReport
